import React, { useEffect, useRef, forwardRef } from "react";
import { ShoppingItemWithProduct } from "@/types";
import TagDisplay from "./attributes/TagDisplay";
import { useThemeConfig } from "@/components/ui/theme-config-provider";

interface ShoppingListRowProps {
  item: ShoppingItemWithProduct;
  onToggle: (item: ShoppingItemWithProduct) => void;
  onCheck?: (id: string, checked: boolean) => void;
  onEdit?: (item: ShoppingItemWithProduct) => void;
  rapidInsert?: boolean;
}

const ShoppingListRow = forwardRef<HTMLLIElement, ShoppingListRowProps>(
  ({ item, onToggle, onCheck, onEdit, rapidInsert = false }, ref) => {
    const { theme } = useThemeConfig();
    const product = item.product || null;

    const pressStartTime = useRef<number | null>(null);

    const handlePointerDown = () => {
      pressStartTime.current = Date.now();
    };

    const handlePointerUp = () => {
      const now = Date.now();
      if (pressStartTime.current && now - pressStartTime.current >= 400) {
        onEdit?.(item);
      }
      pressStartTime.current = null;
    };

    const handleToggle = () => {
      onToggle(item);
      if (onCheck) onCheck(item.id, !item.checked);
    };

    const itemName = product?.name || item.name || "";
    const itemAttributes = Array.isArray(item.attributes) ? item.attributes : [];

    return (
      <li
        ref={ref}
        className="flex items-start gap-2 py-1 border-b pl-[10px]"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        style={{
          borderColor: theme.borderColor || '#e2e8f0',
        }}
      >
        <input
          type="checkbox"
          checked={item.checked}
          onChange={handleToggle}
          className="mt-1"
        />
        <div className="flex-1">
          <div
            className={item.checked ? "line-through" : ""}
            style={{ color: theme.sectionTextColor || '#4b5563' }}
          >
            <span className="font-medium">{itemName}</span>
            {rapidInsert && (
              <span className="ml-2 text-sm font-semibold">
                {item.quantity} {item.unit}
              </span>
            )}
          </div>
          <TagDisplay attributes={itemAttributes} className="mt-1" />
        </div>
      </li>
    );
  }
);

ShoppingListRow.displayName = "ShoppingListRow";

export default React.memo(ShoppingListRow);
